import {
  VYBY_PRESET_KIND_CAMERA_BODY,
  VYBY_PRESET_KIND_CAMERA_DEPTH_OF_FIELD,
  VYBY_PRESET_KIND_CAMERA_FOCAL,
  VYBY_PRESET_KIND_CAMERA_LENS,
  VYBY_PRESET_KIND_CAMERA_MOTION,
  VYBY_PRESET_KIND_CAMERA_TRANSITION_IN,
  VYBY_PRESET_KIND_CAMERA_TRANSITION_OUT,
} from '@/etc/cameraPresetKinds'

export const DEFAULT_CAMERA_DEPTH_OF_FIELD_ID =
  'depth_of_field.regular'

export const CAMERA_PROFILE_FIELDS = Object.freeze([
  'cameraId',
  'lensSystemId',
  'focalStyleId',
  'depthOfFieldId',
  'transitionInId',
  'motionId',
  'transitionOutId',
])

const REQUIRED_CAMERA_PROFILE_FIELDS = Object.freeze([
  'cameraId',
  'lensSystemId',
  'focalStyleId',
  'depthOfFieldId',
])

const normalizeId = (value) => {
  const id = `${value || ''}`.trim()
  return id || null
}

const cameraCatalogEntry = (catalog, id, expectedKind = '') => {
  const normalizedId = `${id || ''}`
  if (!normalizedId) return null
  if (expectedKind) {
    const entries = Array.isArray(catalog?.byKind?.[expectedKind])
      ? catalog.byKind[expectedKind]
      : []
    return (
      entries.find((entry) => entry?.stableId === normalizedId) || null
    )
  }
  return catalog?.byId?.[normalizedId] || null
}

const cameraSupportsLens = (catalog, cameraId, lensId) => {
  const camera = cameraCatalogEntry(
    catalog,
    cameraId,
    VYBY_PRESET_KIND_CAMERA_BODY,
  )
  return !!camera && camera.compatibleIds.includes(`${lensId || ''}`)
}

const lensSupportsFocalStyle = (catalog, lensId, focalStyleId) => {
  const lens = cameraCatalogEntry(
    catalog,
    lensId,
    VYBY_PRESET_KIND_CAMERA_LENS,
  )
  return !!lens && lens.compatibleIds.includes(`${focalStyleId || ''}`)
}

export const createEmptyCameraProfile = () => ({
  cameraId: null,
  lensSystemId: null,
  focalStyleId: null,
  depthOfFieldId: DEFAULT_CAMERA_DEPTH_OF_FIELD_ID,
  transitionInId: null,
  motionId: null,
  transitionOutId: null,
})

export const normalizeCameraProfile = (value) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  return CAMERA_PROFILE_FIELDS.reduce((profile, field) => {
    profile[field] =
      field === 'depthOfFieldId'
        ? normalizeId(value[field]) || DEFAULT_CAMERA_DEPTH_OF_FIELD_ID
        : normalizeId(value[field])
    return profile
  }, {})
}

export const cloneCameraProfile = (value) => {
  const normalized = normalizeCameraProfile(value)
  return normalized ? { ...normalized } : null
}

export const cameraProfileEquals = (left, right) => {
  const a = normalizeCameraProfile(left)
  const b = normalizeCameraProfile(right)
  if (!a || !b) return a === b
  return CAMERA_PROFILE_FIELDS.every((field) => a[field] === b[field])
}

export const isCameraProfileComplete = (value) => {
  const profile = normalizeCameraProfile(value)
  return (
    !!profile &&
    REQUIRED_CAMERA_PROFILE_FIELDS.every((field) => !!profile[field])
  )
}

export const resolveEffectiveCameraProfile = ({
  sectionProfile = null,
  sectionSuppressed = false,
  nodeProfile = null,
  nodeSuppressed = false,
  projectProfile = null,
} = {}) => {
  if (sectionSuppressed) return null
  const section = cloneCameraProfile(sectionProfile)
  if (section) return section
  if (nodeSuppressed) return null
  return (
    cloneCameraProfile(nodeProfile) ||
    cloneCameraProfile(projectProfile) ||
    null
  )
}

export const normalizeCameraProfilePresetId = (value) => {
  const id = `${value || ''}`.trim()
  return id || null
}

export const resolveEffectiveCameraProfileBinding = ({
  sectionProfile = null,
  sectionPresetId = null,
  sectionSuppressed = false,
  nodeProfile = null,
  nodePresetId = null,
  nodeSuppressed = false,
  projectProfile = null,
  projectPresetId = null,
} = {}) => {
  if (sectionSuppressed) {
    return { profile: null, presetId: null, source: 'suppressed' }
  }
  const section = cloneCameraProfile(sectionProfile)
  if (section) {
    return {
      profile: section,
      presetId: normalizeCameraProfilePresetId(sectionPresetId),
      source: 'section',
    }
  }
  if (nodeSuppressed) {
    return { profile: null, presetId: null, source: 'suppressed' }
  }
  const candidates = [
    { profile: nodeProfile, presetId: nodePresetId, source: 'node' },
    {
      profile: projectProfile,
      presetId: projectPresetId,
      source: 'project',
    },
  ]
  const resolved = candidates.find((candidate) =>
    normalizeCameraProfile(candidate.profile),
  )
  return resolved
    ? {
        profile: cloneCameraProfile(resolved.profile),
        presetId: normalizeCameraProfilePresetId(resolved.presetId),
        source: resolved.source,
      }
    : { profile: null, presetId: null, source: 'none' }
}

const PROFILE_ENTRY_KINDS = Object.freeze({
  cameraId: VYBY_PRESET_KIND_CAMERA_BODY,
  lensSystemId: VYBY_PRESET_KIND_CAMERA_LENS,
  focalStyleId: VYBY_PRESET_KIND_CAMERA_FOCAL,
  depthOfFieldId: VYBY_PRESET_KIND_CAMERA_DEPTH_OF_FIELD,
  transitionInId: VYBY_PRESET_KIND_CAMERA_TRANSITION_IN,
  motionId: VYBY_PRESET_KIND_CAMERA_MOTION,
  transitionOutId: VYBY_PRESET_KIND_CAMERA_TRANSITION_OUT,
})

export const validateCameraProfile = (value, catalog) => {
  const profile = normalizeCameraProfile(value)
  const missingFields = []
  const missingIds = []
  const missingRequiredIds = []
  const incompatibleFields = []

  if (!profile) {
    return {
      ok: false,
      profile: null,
      missingFields: [...REQUIRED_CAMERA_PROFILE_FIELDS],
      missingIds,
      missingRequiredIds,
      incompatibleFields,
    }
  }

  REQUIRED_CAMERA_PROFILE_FIELDS.forEach((field) => {
    if (!profile[field]) missingFields.push(field)
  })
  CAMERA_PROFILE_FIELDS.forEach((field) => {
    const id = profile[field]
    if (id && !cameraCatalogEntry(catalog, id, PROFILE_ENTRY_KINDS[field])) {
      missingIds.push(id)
      if (REQUIRED_CAMERA_PROFILE_FIELDS.includes(field)) {
        missingRequiredIds.push(id)
      }
    }
  })

  if (
    profile.cameraId &&
    profile.lensSystemId &&
    !cameraSupportsLens(catalog, profile.cameraId, profile.lensSystemId)
  ) {
    incompatibleFields.push('lensSystemId')
  }
  if (
    profile.lensSystemId &&
    profile.focalStyleId &&
    !lensSupportsFocalStyle(
      catalog,
      profile.lensSystemId,
      profile.focalStyleId,
    )
  ) {
    incompatibleFields.push('focalStyleId')
  }

  return {
    ok:
      missingFields.length === 0 &&
      missingRequiredIds.length === 0 &&
      incompatibleFields.length === 0,
    profile,
    missingFields,
    missingIds,
    missingRequiredIds,
    incompatibleFields,
  }
}

const promptPhraseFor = (catalog, id, kind) =>
  cameraCatalogEntry(catalog, id, kind)?.promptPhrase || ''

const asSentence = (value) => {
  const text = `${value || ''}`.trim()
  if (!text) return ''
  return /[.!?]$/.test(text) ? text : `${text}.`
}

export const composeCameraProfilePromptParts = (value, catalog) => {
  const validation = validateCameraProfile(value, catalog)
  if (!validation.ok) return { prefix: '', suffix: '' }
  const profile = validation.profile

  const transitionIn = promptPhraseFor(
    catalog,
    profile.transitionInId,
    VYBY_PRESET_KIND_CAMERA_TRANSITION_IN,
  )
  const camera = promptPhraseFor(
    catalog,
    profile.cameraId,
    VYBY_PRESET_KIND_CAMERA_BODY,
  )
  const lens = promptPhraseFor(
    catalog,
    profile.lensSystemId,
    VYBY_PRESET_KIND_CAMERA_LENS,
  )
  const focal = promptPhraseFor(
    catalog,
    profile.focalStyleId,
    VYBY_PRESET_KIND_CAMERA_FOCAL,
  )
  const depthOfField = promptPhraseFor(
    catalog,
    profile.depthOfFieldId,
    VYBY_PRESET_KIND_CAMERA_DEPTH_OF_FIELD,
  )
  const motion = promptPhraseFor(
    catalog,
    profile.motionId,
    VYBY_PRESET_KIND_CAMERA_MOTION,
  )
  const transitionOut = promptPhraseFor(
    catalog,
    profile.transitionOutId,
    VYBY_PRESET_KIND_CAMERA_TRANSITION_OUT,
  )

  return {
    prefix: [
      asSentence(transitionIn),
      asSentence(
        `Camera profile: ${[camera, lens, focal, depthOfField].filter(Boolean).join(', ')}`,
      ),
      asSentence(motion),
    ]
      .filter(Boolean)
      .join(' '),
    suffix: asSentence(transitionOut),
  }
}

export const composeCameraProfilePhrase = (value, catalog) => {
  const parts = composeCameraProfilePromptParts(value, catalog)
  return [
    parts.prefix,
    parts.suffix,
  ]
    .filter(Boolean)
    .join(' ')
}

export const applyCameraProfileToPrompt = (text, value) => {
  const body = `${text || ''}`.trim()
  const parts =
    value && typeof value === 'object'
      ? {
          prefix: `${value.prefix || ''}`.trim(),
          suffix: `${value.suffix || ''}`.trim(),
        }
      : { prefix: `${value || ''}`.trim(), suffix: '' }
  return [
    parts.prefix,
    body,
    parts.suffix,
  ]
    .filter(Boolean)
    .join('\n\n')
}

export const cameraProfileLabel = (value, catalog) => {
  const profile = normalizeCameraProfile(value)
  if (!profile) return ''
  return [
    cameraCatalogEntry(catalog, profile.cameraId)?.name,
    cameraCatalogEntry(catalog, profile.lensSystemId)?.name,
    cameraCatalogEntry(catalog, profile.focalStyleId)?.name,
    cameraCatalogEntry(catalog, profile.depthOfFieldId)?.name,
  ]
    .filter(Boolean)
    .join(' · ')
}
